<template>
  <section
    data-cy="handover-process-view"
    :data-view-ready="!loading && !isSaving">
    <div>
      <coozzy-page-title
        :title="$t('message.processHandover.title') + ' ' + buildingName" />
      <div
        class="d-md-none">
        <h5>
          {{ objectAddress }}
        </h5>
        <h5>
          {{ objectNameType }}
        </h5>
      </div>
      <div
        class="col-12 d-none d-md-block">
        <h5 data-cy="handover-process-object-address">
          {{ objectAddress }}
        </h5>
        <h5 data-cy="handover-process-object-name">
          {{ objectNameType }}
        </h5>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <div
          v-if="isSaving"
          class="text-center">
          <coozzy-spinner />
        </div>
        <form-wizard
          v-else
          id="wizard"
          :ref="'wizard-form'"
          :class="{ 'wizard-nav-disabled': disabledSave }"
          :hide-buttons="loading"
          title=""
          subtitle=""
          step-size="xs"
          color="rgb(0, 68, 95)"
          @on-change="handleTabChange">
          <template #title>
            <span />
          </template>
          <tab-content :title="$t('message.processHandover.participants')">
            <div
              v-if="loading"
              class="text-center">
              <coozzy-spinner />
            </div>
            <handover-other-tenancies
              v-else-if="!loading && objectsWithSameTenants.length > 0"
              :objects-with-same-tenants="availableObjects"
              :same-tenancies-by-object="sameTenanciesByObject"
              :object-id="objectId"
              :preselected-object-ids="preselectedObjectIds"
              @selected-object="selectedObject" />
            <div class="row">
              <div class="col-3">
                <label>{{ $t('message.settings.template.template') }} </label>
                <coozzy-form-select
                  v-model="templateId"
                  name="templateId"
                  data-cy="handover-process-template-select"
                  class="select-entries float-right">
                  <option
                    :value="null">
                    -
                  </option>
                  <option
                    v-for="(template, index) in filteredTemplates"
                    :key="index + template.id"
                    :value="template.id">
                    {{ template.name }}
                  </option>
                </coozzy-form-select>
              </div>
              <div class="col-3">
                <label>{{ $t('message.tableColumnHeadings.adminAssignee') }} </label>
                <coozzy-form-select
                  v-model="handoverObject.assigneeContactId"
                  name="assigneeMarketing"
                  data-cy="handover-process-assignee-select"
                  class="select-entries float-right">
                  <option
                    v-for="(employee, index) in availableEmployeeContacts"
                    :key="index + employee.id"
                    :value="employee.id">
                    {{ employee.firstName }} {{ employee.lastName }}
                  </option>
                </coozzy-form-select>
              </div>
              <div class="col-3">
                <label>{{ $t('message.calendar.event.start') }}*</label>
                <coozzy-form-input
                  id="startDate"
                  ref="startDate"
                  v-model="scheduledDate"
                  data-cy="handover-process-start-date-input"
                  :state="$v.scheduledDate.$dirty && $v.scheduledDate.$error ? false : null"
                  type="date" />
              </div>
              <div
                class="col-3">
                <label>{{ $t('message.calendar.event.time') }}*</label>
                <coozzy-form-select
                  id="startTime"
                  v-model="scheduledTime"
                  data-cy="handover-process-start-time-select"
                  :state="$v.scheduledTime.$dirty && $v.scheduledTime.$error ? false : null">
                  <option
                    v-for="t in timeList"
                    :key="'start' + t"
                    :value="t">
                    {{ t }}
                  </option>
                </coozzy-form-select>
              </div>
              <template
                v-if="!loading && objectsWithSameTenantsComputed.length > 0">
                <template
                  v-for="(obj, index) in objectsWithSameTenantsComputed">
                  <div
                    :key="'obj-' + index"
                    class="col-12"
                    data-cy="handover-process-object-collapse">
                    <hr class="mt-3 mb-3">
                    <div
                      id="titleObject"
                      :ref="`titleObject`"
                      v-b-toggle="`titleObject-${obj.id}`"
                      class="collapse-title">
                      <h5
                        class="cursor-pointer"
                        data-cy="handover-process-object-collapse-title">
                        {{ obj.numericId + ' - ' + obj.name }}
                      </h5>
                    </div>
                    <b-collapse
                      :id="`titleObject-${obj.id}`"
                      :key="`object-${obj.id}`"
                      :ref="`collapse-${obj.id}`"
                      :visible="true">
                      <div class="row">
                        <div
                          class="col-6 col-md-6 mt-3"
                          data-cy="handover-process-move-in-section">
                          <handover-tenancy-move
                            :ref="'moving-in-tenancies-card'"
                            :key="filterSameTenanciesMovingInBySelectedObject.length"
                            :object="obj"
                            :from-handover="'moveIn'"
                            :available-contact="detailsOfTenants"
                            :move-tenancies="!tenancyAfterVacancy ? filterSameTenanciesMovingInBySelectedObject : currentTenancySigned ? filterSameTenanciesMovingInBySelectedObjectAndNoSigned : filterSameTenanciesMovingInBySelectedObject"
                            :type="'TENANT'"
                            :preselected-tenancy="getPreselectedTenancyForObject(obj.id, 'moveIn')"
                            :preselected-contact-persons="preselectedContactPersons"
                            :select-tenancy-status="isObjectTenancyInvalid(obj.id) ? false : null"
                            @move-in-tenancy-selected="moveInTenancySelected"
                            @move-in-contact-persons-selected="moveInContactPersonsSelected"
                            @contact-updated="contactUpdated" />
                        </div>
                        <div
                          class="col-6 col-md-6 mt-3"
                          data-cy="handover-process-move-out-section">
                          <handover-tenancy-move
                            :key="filterSameTenanciesMovingOutBySelectedObject.length"
                            :ref="'moving-out-tenancies-card'"
                            :from-handover="'moveOut'"
                            :object="obj"
                            :available-contact="detailsOfTenants"
                            :move-tenancies="filterSameTenanciesMovingOutBySelectedObject"
                            :type="'TENANT'"
                            :preselected-tenancy="getPreselectedTenancyForObject(obj.id, 'moveOut')"
                            :preselected-contact-persons="preselectedContactPersons"
                            :select-tenancy-status="isObjectTenancyInvalid(obj.id) ? false : null"
                            @move-out-tenancy-selected="moveOutTenancySelected"
                            @move-out-contact-persons-selected="moveOutContactPersonsSelected"
                            @contact-updated="contactUpdated" />
                        </div>
                      </div>
                    </b-collapse>
                  </div>
                </template>
              </template>
            </div>
          </tab-content>
          <template
            v-for="(item, index) in selectedObjectToProcess">
            <tab-content
              :key="'tabRoom-' + index"
              :title="getTabTitle('rooms', item)">
              <div
                v-if="loading"
                class="text-center">
                <coozzy-spinner />
              </div>
              <div
                v-else-if="item"
                class="row">
                <div class="col-md-12">
                  <h3>{{ item.name }}</h3>
                </div>
                <handover-rooms-step
                  v-if="activeTab !== 0"
                  :key="`${item.id}-rooms-${selectedRooms.length}`"
                  :object="item"
                  :self-rooms="rooms.filter(x => x.referenceId === item.id).filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)"
                  :pre-selected-rooms="selectedRooms.find(x => x.object === item.id).rooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)"
                  :suggestion-rooms="suggestionRooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)"
                  :template-object="templateObject"
                  @selected-rooms="storeSelectedRooms"
                  @deleted-rooms="storeDeletedRooms" />
              </div>
            </tab-content>
            <tab-content
              :key="'tabDevice-' +index"
              :title="getTabTitle('devices', item)">
              <div
                v-if="loading"
                class="text-center">
                <coozzy-spinner />
              </div>
              <handover-devices-step
                v-if="selectedRooms.find(x => x.object === item.id) && activeTab !== 0"
                :key="`${item.id}-devices-${selectedRooms.length}`"
                :object="item"
                :init-key="initKey"
                :template-object="templateObject"
                :devices="listDevices.filter(x => x.referenceId === item.id && !deletedDevices.some(d => d.devices.includes(x.id)))"
                :pre-selected-rooms="selectedRooms.find(x => x.object === item.id).rooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)"
                :handover-deleted-device-ids="deletedDevices.find(x => x.object === item.id)?.devices"
                :reset-devices-without-rooms="resetDevicesWithoutRooms"
                :suggestion-devices="suggestionDevices.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)"
                :suggestion-rooms="suggestionRooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)"
                @add-init-key="addInitKey"
                @selected-rooms="storeSelectedRooms"
                @deleted-devices="storeDeletedDevices"
                @devices-without-rooms="storeDevicesWithoutRooms"
                @reset-complete="resetDevicesWithoutRooms = false" />
            </tab-content>
            <tab-content
              :key="'tabKeys-' +index"
              :title="getTabTitle('keys', item)">
              <div
                v-if="loading"
                class="text-center">
                <coozzy-spinner />
              </div>
              <handover-keys-step
                v-else-if="selectedRooms.find(x => x.object === item.id) && activeTab % 3 === 0 && activeTab !== 0"
                :key="`${item.id}-keys-${selectedRooms.length}`"
                :object="item"
                :init-keys="initKeys.filter(k => k.referenceId === item.id && !(deletedDevices.find(x => x.object === item.id)?.devices || []).includes(k.id))"
                :pre-selected-rooms="selectedRooms.find(x => x.object === item.id).rooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)"
                :handover-deleted-device-ids="deletedDevices.find(x => x.object === item.id)?.devices"
                :template-object="templateObject"
                :suggestion-devices="suggestionDevices.filter(w => (w.type === 'DEVICE_TYPE_LOCKING_KEY' || w.type === 'DEVICE_TYPE_LOCKING_WIRELESS_TRANSMITTER') && w.roomId === '')"
                @add-init-key="addInitKey"
                @selected-rooms="storeSelectedRooms"
                @deleted-keys="storeDeletedDevices"
                @update-keys="storeDevicesWithoutRooms" />
            </tab-content>
          </template>
          <template
            #footer="props">
            <template v-if="!loading">
              <div class="wizard-footer-left">
                <wizard-button
                  v-if="props.activeTabIndex === 0"
                  data-cy="handover-process-cancel-button"
                  :style="props.fillButtonStyle"
                  @click.native="$router.back()">
                  {{ $t('message.generic.cancel') }}
                </wizard-button>
                <wizard-button
                  v-else
                  data-cy="handover-process-back-button"
                  :style="props.fillButtonStyle"
                  :disabled="disabledSave"
                  @click.native="backClicked(props)">
                  {{ $t('message.generic.back') }}
                </wizard-button>
              </div>
              <div class="wizard-footer-right">
                <wizard-button
                  v-if="!props.isLastStep && props.activeTabIndex === 0"
                  class="wizard-footer-right"
                  data-cy="handover-process-next-button"
                  :style="props.fillButtonStyle"
                  :disabled="disabledSave || devicesLoadFailed"
                  @click.native="nextStep(props)">
                  {{ $t('message.generic.next') }}
                </wizard-button>
                <wizard-button
                  v-else-if="props.isLastStep"
                  class="wizard-footer-right"
                  data-cy="handover-process-save-button"
                  :style="props.fillButtonStyle"
                  :disabled="disabledSave"
                  @click.native="nextStep(props)">
                  <template>
                    {{ $t('message.generic.form.save') }}
                  </template>
                </wizard-button>
                <wizard-button
                  v-else
                  class="wizard-footer-right"
                  data-cy="handover-process-save-and-next-button"
                  :style="props.fillButtonStyle"
                  :disabled="disabledSave"
                  @click.native="nextStep(props)">
                  {{ $t('message.enterTermination.saveAndNext') }}
                </wizard-button>
                <wizard-button
                  class="wizard-footer-right mr-2"
                  data-cy="handover-process-save-draft-button"
                  :style="props.fillButtonStyle"
                  :disabled="disabledSave"
                  @click.native="saveStep(props, true)">
                  {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
                </wizard-button>
              </div>
            </template>
          </template>
        </form-wizard>
      </div>
    </div>
    <b-modal
      id="confirmationModal"
      ref="confirmationModal"
      data-cy="handover-process-cancel-confirm-modal"
      no-close-on-backdrop
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer>
      <p>{{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}</p>
      <div class="col">
        <coozzy-button
          data-cy="handover-process-cancel-confirm-abort-button"
          size="small"
          class="border mb-0"
          design="transparent"
          @click="$bvModal.hide('confirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          data-cy="handover-process-cancel-confirm-discard-button"
          size="small"
          class="float-right mb-0"
          design="green"
          @click="confirmCancellation">
          {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>

<script lang="ts">
import Vue from 'vue'
import { Route } from 'vue-router'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import RoomApi from '@/misc/apis/RoomApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import ContactApi from '@/misc/apis/ContactApi'
import HandoverTenancyMove from '@/handover/component/HandoverTenancyMove.vue'
import { formatting } from '@/mixins/formatting'
import { obj } from '@/mixins/object'
import { onboarding } from '@/mixins/onboarding'
import { routeChecks } from '@/mixins/routeChecks'
import { string } from '@/mixins/string'
import HandoverOtherTenancies from '@/handover/HandoverOtherTenancies.vue'
import HandoverDevicesStep from '@/handover/component/HandoverDevicesStep.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { user } from '@/mixins/user'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle.vue'
import HandoverRoomsStep from '@/handover/component/HandoverRoomsStep.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { handover } from '@/mixins/handover'
import { deviceUtils } from '@/mixins/deviceUtils'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import { required } from 'vuelidate/lib/validators'
import HandoverKeysStep from '@/handover/component/HandoverKeysStep.vue'
import { emptyHandoverProtocol } from '@/types/helper'
import HandoverProtocolApi from '@/misc/apis/HandoverProtocolApi'
import { mapGetters } from 'vuex'
import {
  ObjectobjectObject,
  ObjectbuildingBuilding,
  ObjecttenancyTenancy,
  ObjectdeviceDevice,
  ObjectroomRoom,
  ObjecthandoverProtocolTemplate,
  ObjecthandoverProtocolParticipant,
  HandoverProtocolHandoverProtocol,
  HandoverProtocolWriteTenancyReference,
  TypeDate,
  TypeTimeOfDay,
  Propertyv2Property,
  V2User,
  V2Person
} from '@/types/api'
import { HandoverDevice } from '@/types/models'

type ProcessHandoverDevice = HandoverDevice & ObjectdeviceDevice

interface ProcessHandoverRoom extends ObjectroomRoom {
  devices?: ProcessHandoverDevice[]
  edited?: boolean
}

interface ProcessHandoverObject {
  assigneeContactId: string | null
  devicesWithoutRoom: ProcessHandoverDevice[]
  draft?: boolean
  movingInTenancyReferences: HandoverProtocolWriteTenancyReference[]
  movingOutTenancyReferences: HandoverProtocolWriteTenancyReference[]
  note: string
  ownerId?: string | null
  participants: ObjecthandoverProtocolParticipant[]
  rooms: { id: string; devices: ProcessHandoverDevice[]; name: string; note: string }[]
  scheduledDate: TypeDate | null
  scheduledTime: TypeTimeOfDay | null
  templateId: string | null
  id?: string
  finished?: boolean
  status?: string
}

interface SelectedRoomEntry {
  rooms: ProcessHandoverRoom[]
  object: string
}

interface DeletedRoomEntry {
  rooms: string[]
  object: string
}

interface DeletedDeviceEntry {
  devices: string[]
  object: string
}

interface SelectedTenancyEntry {
  tenancy: ObjecttenancyTenancy
  objectId: string
}

interface ContractEntry {
  value: string[]
  contracts: string[]
  objectId: string
}

interface TemplateDataCache {
  rooms: ObjectroomRoom[]
  devices: ProcessHandoverDevice[]
}

interface HandoverValidatorContext {
  scheduledDate: string
  scheduledTime: string
  handoverId: string
  objectId: string
  handoverProtocols: HandoverProtocolHandoverProtocol[]
  $options: { filters: Record<string, (val: TypeDate | TypeTimeOfDay) => string> }
}

function tenancyTenantContactSetsAlign(tenancy: ObjecttenancyTenancy, anchorContactIds: string[]): boolean {
  if (!tenancy.tenant?.contactIds?.length || !anchorContactIds.length) {
    return false
  }
  const anchor = new Set(anchorContactIds)
  const current = new Set(tenancy.tenant.contactIds)
  return anchor.size === current.size && [...anchor].every(id => current.has(id))
}

function isNotDuplicated(this: HandoverValidatorContext): boolean {
  if (!this.scheduledDate || !this.scheduledTime || this.handoverId !== '') {
    return true
  }
  // check if the new handover has not the same date and time as the existing handover
  const handover = this.handoverProtocols.find(x => {
    const sameDate = this.$options.filters.objectToDateInput(x.scheduledDate) === this.scheduledDate
    const sameTime = this.$options.filters.objectToTime(x.scheduledTime) === this.scheduledTime
    // const sameOutTenancy = x.movingOutTenancyReferences[0]?.tenancyId === this.selectedTenancyMovingOut?.find(x => x.objectId === this.objectId)?.id
    // const sameInTenancy = x.movingInTenancyReferences[0]?.tenancyId === this.selectedTenancyMovingIn?.find(x => x.objectId === this.objectId)?.id
    const sameObject = x.movingInTenancyReferences[0]?.objectId === this.objectId || x.movingOutTenancyReferences[0]?.objectId === this.objectId
    return sameDate && sameTime && sameObject
  })
  return !handover
}

export default Vue.extend({
  name: 'ProcessHandoverView',
  components: {
    CoozzyFormInput,
    CoozzyFormSelect,
    HandoverRoomsStep,
    CoozzyPageTitle,
    CoozzyButton,
    CoozzySpinner,
    HandoverOtherTenancies,
    HandoverTenancyMove,
    HandoverDevicesStep,
    HandoverKeysStep
  },
  mixins: [formatting, string, obj, onboarding, routeChecks, user, handover, deviceUtils],
  beforeRouteEnter(to: Route, from: Route, next) {
    next(vm => {
      (vm as Vue & { prevRoute: Route | null }).prevRoute = from
    })
  },
  beforeRouteLeave(to: Route, _from: Route, next) {
    this.nextToPath = to
    if (this.isFormDirty && !this.confirmCancellationProcess) {
      this.$refs.confirmationModal.show()
    } else {
      next()
    }
  },
  props: {
    handoverId: {
      type: String,
      default: ''
    },
    objectId: {
      type: String,
      required: true
    },
    tenancyId: {
      type: [String, Number],
      default: ''
    },
    buildingId: {
      type: [String, Number],
      default: ''
    },
    tenancyAfterVacancy: {
      type: Boolean,
      default: false
    },
    currentTenancySigned: {
      type: Object,
      default: null
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.processHandover.title')
    }
  },
  data() {
    return {
      templateObject: null as ObjecthandoverProtocolTemplate | null,
      suggestionRooms: [] as ObjectroomRoom[],
      suggestionDevices: [] as ProcessHandoverDevice[],
      templateRooms: [] as ObjectroomRoom[],
      templateDevices: [] as ProcessHandoverDevice[],
      templateId: null as string | null,
      loadedTemplatesData: {} as Record<string, TemplateDataCache>,
      property: null as Propertyv2Property | null,
      globalDeletedDevicesIds: [] as string[],
      listHandoverByObjectId: [] as HandoverProtocolHandoverProtocol[],
      initKey: 0 as number,
      isSaving: false as boolean,
      draftStatus: false as boolean,
      activeTab: 0 as number,
      devicesWithoutRooms: [] as ProcessHandoverDevice[],
      allTenancies: [] as ObjecttenancyTenancy[],
      activeTabIndex: 0 as number,
      listDevices: [] as ProcessHandoverDevice[],
      availableObjects: [] as ObjectobjectObject[],
      availableTemplates: [] as ObjecthandoverProtocolTemplate[],
      deletedRooms: [] as DeletedRoomEntry[],
      deletedDevices: [] as DeletedDeviceEntry[],
      scheduledDate: '' as string,
      scheduledTime: '' as string,
      handoverObject: {
        assigneeContactId: null,
        devicesWithoutRoom: [],
        draft: false,
        movingInTenancyReferences: [],
        movingOutTenancyReferences: [],
        note: '',
        ownerId: null,
        participants: [],
        rooms: [],
        scheduledDate: null,
        scheduledTime: null,
        templateId: null
      } as ProcessHandoverObject,
      disabledSave: false as boolean,
      anyContract: false as boolean,
      addDocumentToContact: false as boolean,
      contactIds: [] as string[],
      detailsOfTenants: [] as ObjectobjectObject[],
      lastTenancy: null as ObjecttenancyTenancy | null,
      loading: false as boolean,
      object: null as ObjectobjectObject | null,
      rooms: [] as ProcessHandoverRoom[],
      objectsWithSameTenants: [] as ObjectobjectObject[],
      sameTenanciesMovingOut: [] as ObjecttenancyTenancy[],
      sameTenanciesMovingIn: [] as ObjecttenancyTenancy[],
      selectedEntriesContract: [] as ContractEntry[],
      selectedEntriesReview: [] as string[],
      selectedObjectToProcess: [] as ObjectobjectObject[],
      preventChangesInSelectedObjectToProcess: false as boolean,
      tenancy: null as ObjecttenancyTenancy | null,
      visibleModal: false as boolean,
      confirmationReviewContacts: false as boolean,
      confirmCancellationProcess: false as boolean,
      isFormDirty: false as boolean,
      dataLoaded: false as boolean,
      nextToPath: null as Route | null,
      selectedRooms: [] as SelectedRoomEntry[],
      building: null as ObjectbuildingBuilding | null,
      listObjects: [] as ObjectobjectObject[],
      timeList: [] as string[],
      contact: null as ObjectobjectObject | null,
      navigateVieButtons: false as boolean,
      selectedTenancyMovingIn: [] as SelectedTenancyEntry[],
      selectedTenancyMovingOut: [] as SelectedTenancyEntry[],
      preselectedContactPersons: [] as ObjecthandoverProtocolParticipant[],
      listMovingInContactPersons: [] as ObjectobjectObject[],
      listMovingOutContactPersons: [] as ObjectobjectObject[],
      resetDevicesWithoutRooms: false as boolean,
      devicesLoadFailed: false as boolean,
      loadedDeviceReferenceIds: [] as string[],
      devicesLoadPromise: null as Promise<void> | null,
      prevRoute: null as Route | null
    }
  },
  computed: {
    filterSameTenanciesMovingInBySelectedObject(): ObjecttenancyTenancy[] {
      // filter the sameTenanciesMovingIn by the selected objects
      return this.sameTenanciesMovingIn.filter(r => this.selectedObjectToProcess.map(x => x.id).includes((r as ObjecttenancyTenancy & { objectId: string }).objectId))
    },
    filterSameTenanciesMovingInBySelectedObjectAndNoSigned(): ObjecttenancyTenancy[] {
      // filter the sameTenanciesMovingIn by the selected objects and get only next tenancy (currentTenancySigned)
      const s = (this.sameTenanciesMovingIn.concat(this.sameTenanciesMovingOut)).filter(r => r?.id === this.currentTenancySigned?.id)
      const array = this.filterSameTenanciesMovingInBySelectedObject.concat(s)
      // remove duplicated object on array
      return array.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
    },
    filterSameTenanciesMovingOutBySelectedObject(): ObjecttenancyTenancy[] {
      // filter the sameTenanciesMovingOut by the selected objects
      return this.sameTenanciesMovingOut.filter(r => this.selectedObjectToProcess.map(x => x.id).includes((r as ObjecttenancyTenancy & { objectId: string }).objectId))
    },
    availableEmployees(): V2User[] {
      return this.getEmployees().filter((e: V2User) => !e.blocked && !e.deleted)
    },
    availableEmployeeContacts(): V2Person[] {
      return this.availableEmployees
        .map((e) => this.employeeContacts.find((c) => c.email?.toLowerCase() === e.email?.toLowerCase()) as V2Person)
        .filter(e => !!e)
    },
    objectAddress(): string {
      if (this.object) {
        return this.$t('message.onBoarding.buildings.address') + ': ' + this.object.address.street + ' ' + this.object.address.streetNumber + ', ' + this.object.address.zip + ' ' + this.object.address.city
      }
      return '-'
    },
    objectNameType(): string {
      if (this.object) {
        return this.$t('message.onBoarding.buildings.objects.titleObject') + ': ' + this.capitalizeString(this.object.type) + ' - ' + this.object.name
      }
      return '-'
    },
    sameTenanciesByObject(): ObjecttenancyTenancy[] {
      return this.sameTenanciesMovingIn.concat(this.sameTenanciesMovingOut).concat(this.allTenancies).filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
    },
    preselectedObjectIds(): string[] {
      if (this.handoverId && this.handoverObject) {
        // For existing handover: derive from tenancy references
        const objectIdsFromHandover = [
          ...this.handoverObject.movingInTenancyReferences.map(ref => ref.objectId),
          ...this.handoverObject.movingOutTenancyReferences.map(ref => ref.objectId)
        ].filter((v, i, a) => a.indexOf(v) === i)
        const otherIds = objectIdsFromHandover.filter(id => id !== this.objectId)
        return [this.objectId, ...otherIds]
      }
      // For new handover: return only the prepare handover selected object
      return [this.objectId]
    },
    prevRoutePath(): string {
      return this.prevRoute ? this.prevRoute.path : '/'
    },
    prevRouteQuery(): Record<string, string | (string | null)[]> | null {
      return this.prevRoute ? this.prevRoute.query : null
    },
    buildingName(): string {
      if (this.building) {
        return this.building.name
      } else {
        return '-'
      }
    },
    objectsWithSameTenantsComputed(): ObjectobjectObject[] {
      return this.objectsWithSameTenants.filter(x => this.selectedObjectToProcess.map(o => o.id).includes(x.id))
    },
    invalidMovingTenancies(): boolean {
      return this.objectsWithSameTenantsComputed.some(obj =>
        !this.selectedTenancyMovingIn.some(x => x.objectId === obj.id) &&
        !this.selectedTenancyMovingOut.some(x => x.objectId === obj.id)
      )
    },
    initKeys(): ProcessHandoverDevice[] {
      const sourceDevices = this.devicesWithoutRooms || []
      return sourceDevices
        .filter(d => ['DEVICE_TYPE_LOCKING_KEY', 'DEVICE_TYPE_LOCKING_WIRELESS_TRANSMITTER'].includes(d.type))
        .map(k => this.listDevices.find(d => d.id === k.id) || k)
    },
    filteredTemplates(): ObjecthandoverProtocolTemplate[] {
      // keep only templates that have a reference to the current property or no references at all
      if (!this.availableTemplates || this.availableTemplates.length === 0) {
        return []
      }
      if (!this.property) {
        return this.availableTemplates.filter(t => t.templateReferences.length === 0)
      }
      // filter templates that have a reference to the current property
      if (this.property && this.property.id) {
        return this.availableTemplates.filter(t => t.templateReferences.find(r => r.propertyId === this.property!.id) || t.templateReferences.length === 0)
      }
      // sort templates by favorite
      const templates = [...this.availableTemplates]
      return templates.sort((a, b) => b.favorite - a.favorite)
    }
  },
    watch: {
    handoverObject: {
      deep: true,
      handler() {
        if (this.dataLoaded) {
          this.isFormDirty = true
        }
      }
    },
    scheduledDate(newVal: string, oldVal: string) {
      if (this.dataLoaded && newVal !== oldVal) {
        this.isFormDirty = true
      }
    },
    scheduledTime(newVal: string, oldVal: string) {
      if (this.dataLoaded && newVal !== oldVal) {
        this.isFormDirty = true
      }
    },
    availableEmployeeContacts(newVal: V2Person[], oldVal: V2Person[]) {
      if (newVal.length > 0 && JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        this.handoverObject.assigneeContactId = this.handoverObject.assigneeContactId || this.findAssigneeContactIdByUserEmail()
      }
    },
    templateId(newVal: string | null) {
      if (this.dataLoaded) {
        this.isFormDirty = true
      }
      if (newVal) {
        const template = this.availableTemplates.find(t => t.id === newVal)
        if (template) {
          this.templateObject = template
          this.loadTemplateDevicesAndRooms(newVal)
        }
      } else {
        this.templateObject = null
        this.suggestionRooms = []
        this.suggestionDevices = []
      }
    },
    '$i18n.locale'(newLocale: string, oldLocale: string) {
      if (newLocale !== oldLocale) {
        this.reloadLocalizedData()
      }
    }
  },
  mounted() {
    this.loadTemplate()
    this.getListTimes()
    this.loadHandoverData()
  },
  methods: {
    ...mapGetters('employee', ['getEmployees']),
    reloadLocalizedData() {
      // Reload template suggestions if a template is selected
      if (this.templateId) {
        delete this.loadedTemplatesData[this.templateId]
        this.loadTemplateDevicesAndRooms(this.templateId)
      }
    },
    loadTemplate() {
      HandoverProtocolApi.handoverProtocolListTemplates(this.accountId)
        .then(response => {
          this.availableTemplates = response.templates
          // If templateId is already set, load its devices/rooms
          if (this.templateId) {
            this.loadTemplateDevicesAndRooms(this.templateId)
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
        })
    },
    updateTemplateSuggestions(template: ObjecthandoverProtocolTemplate) {
      const cachedData = this.loadedTemplatesData[template.id] || { rooms: [], devices: [] }
      this.suggestionRooms = cachedData
        .rooms.filter(r => template.rooms?.map(x => x.id).includes(r.id) || false)
        .sort((a, b) => a.order - b.order)
      this.suggestionDevices = cachedData
        .devices.filter(d =>
          template.rooms?.map(x => x.deviceIds).flat().includes(d.id) ||
          template.devicesWithoutRoomIds?.includes(d.id)
        )
        .sort((a, b) => a.order - b.order)
    },
    loadTemplateDevicesAndRooms(templateId: string) {
      const template = this.availableTemplates.find(t => t.id === templateId)
      if (!template) {
        return
      }

      if (this.loadedTemplatesData[templateId]) {
        this.updateTemplateSuggestions(template)
        return
      }

      this.$set(this.loadedTemplatesData, templateId, { rooms: [], devices: [] })

      let rooms = []
      let devices = []

      if (template.rooms && template.rooms.length > 0) {
        rooms = template.rooms
        devices = template.rooms.map(r => r.deviceIds).flat().concat(template.devicesWithoutRoomIds || [])
        // Remove duplicates
        rooms = rooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
        devices = devices.filter((v, i, a) => a.findIndex(t => (t === v)) === i)
      } else if (template.devicesWithoutRoomIds && template.devicesWithoutRoomIds.length > 0) {
        devices = template.devicesWithoutRoomIds.filter((v, i, a) => a.findIndex(t => (t === v)) === i)
      }

      const promises = []

      if (rooms.length > 0) {
        promises.push(this.loadRoomByIds(rooms.map(r => r.id), templateId))
      }
      if (devices.length > 0) {
        promises.push(this.loadDevicesByIds(devices, templateId))
      }

      if (promises.length > 0) {
        Promise.all(promises).then(() => {
          this.updateTemplateSuggestions(template)
        })
      } else {
        this.updateTemplateSuggestions(template)
      }
    },
    async loadHandoverData() {
      this.loading = true
      const t = this
      try {
        const rspListHandoverByObj = await HandoverProtocolApi.handoverProtocolServiceListByObjectIds([this.objectId])
        this.listHandoverByObjectId = rspListHandoverByObj.protocols

        if (this.handoverId) {
          const rspListHandover = await HandoverProtocolApi.handoverProtocolListByIds([this.handoverId])
          if (rspListHandover.protocols.length === 0) {
            Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
            return
          }
          this.handoverObject = rspListHandover.protocols[0]
          this.handoverObject.assigneeContactId = this.handoverObject.assigneeContactId || this.findAssigneeContactIdByUserEmail()

          if (this.handoverObject.templateId) {
            this.templateId = this.handoverObject.templateId
          }

          if (this.handoverObject.scheduledDate) {
            this.scheduledDate = this.$options.filters.objectToDateInput(this.handoverObject.scheduledDate)
          }
          if (this.handoverObject.scheduledTime) {
            const hours = this.handoverObject.scheduledTime.hours.toString().padStart(2, '0')
            const minutes = this.handoverObject.scheduledTime.minutes.toString().padStart(2, '0')
            this.scheduledTime = hours + ':' + minutes
          }

          if (this.handoverObject.devicesWithoutRoom?.length > 0) {
            this.devicesWithoutRooms = [...this.handoverObject.devicesWithoutRoom]
          }

          // Extract objectIds from tenancy references for pre-selection
          const objectIdsFromHandover = [
            ...this.handoverObject.movingInTenancyReferences.map(ref => ref.objectId),
            ...this.handoverObject.movingOutTenancyReferences.map(ref => ref.objectId)
          ].filter((v, i, a) => a.indexOf(v) === i)

          if (objectIdsFromHandover.length > 0) {
            const rspTenanciesFromHandover = await ObjectApi.getTenancies(objectIdsFromHandover)
            if (rspTenanciesFromHandover.tenancies.length > 0) {
              this.allTenancies = this.allTenancies.concat(rspTenanciesFromHandover.tenancies)
              // Remove duplicates
              this.allTenancies = this.allTenancies.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i)
            }
          }
        }

        let object = this.objectsWithSameTenants.find(o => o.id === this.objectId) || this.availableObjects.find(o => o.id === this.objectId) || null
        if (!object) {
          const rspObject = await ObjectApi.getObject(this.objectId)
          object = rspObject.object
        }

        this.object = object

        if (!this.objectsWithSameTenants.find(o => o.id === object.id)) {
          this.objectsWithSameTenants.push(object)
        }
        if (!this.availableObjects.find(o => o.id === object.id)) {
          this.availableObjects.push(object)
        }

        this.loadBuilding(this.object.buildingId)
        await ObjectApi.getPropertiesByBuildingIds([this.object.buildingId])
          .then(response => {
            this.property = response.properties[0]
          })
        const rspTenancies = await ObjectApi.getTenancies([this.object.id])
        if (rspTenancies.tenancies.length > 0) {
          this.allTenancies = this.allTenancies.concat(rspTenancies.tenancies)
          const terminatedNonVacancy = rspTenancies.tenancies.find(x => x.status === 'TENANCY_STATUS_CURRENT_TERMINATED' && !x.vacancy)
          const nonVacancyWithContact = rspTenancies.tenancies.find(x => !x.vacancy && x.tenant && x.tenant.contactIds && x.tenant.contactIds.length > 0)
          if (terminatedNonVacancy) {
            this.lastTenancy = terminatedNonVacancy
          } else if (nonVacancyWithContact) {
            this.lastTenancy = nonVacancyWithContact
          } else {
            this.lastTenancy = rspTenancies.tenancies.sort(function (a, b) {
              return new Date(t.$options.filters.objectToDateInput(b.endingAt)).getTime() - new Date(t.$options.filters.objectToDateInput(a.endingAt)).getTime()
            })[0]
          }

          if (this.lastTenancy && this.lastTenancy.tenant.contactIds) {
            const rspTenanciesByTenant = await ObjectApi.getTenanciesByTenant(this.lastTenancy.ownerId, this.lastTenancy.tenant.contactIds[0])
            let allReceivedTenancies = rspTenanciesByTenant.tenancies
            this.allTenancies = this.allTenancies.concat(allReceivedTenancies)
            allReceivedTenancies = allReceivedTenancies.concat(rspTenancies.tenancies)
            // remove duplicates
            allReceivedTenancies = allReceivedTenancies.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
            // include all tenancies; for others, only those with matching or overlapping households.
            const lastTenancyContactIds = this.lastTenancy.tenant.contactIds
            allReceivedTenancies = allReceivedTenancies.filter(tenancy => {
              if (tenancy.objectId === this.objectId) return true
              return tenancyTenantContactSetsAlign(tenancy, lastTenancyContactIds)
            })
            // get all contactIds from all tenancies and remove duplicated
            const listContactIds = allReceivedTenancies.map(x => x.tenant.contactIds).flat().filter((v, i, a) => a.findIndex(t => (t === v)) === i)
            // get all contactIds from all tenancies without duplicates
            const uniqueContactIds = [...new Set(listContactIds)]
            if (uniqueContactIds.length > 0) {
              const rspContacts = await ContactApi.contactResolve(uniqueContactIds)
              this.detailsOfTenants = [...rspContacts.persons, ...rspContacts.companies]
            }
            this.sameTenanciesMovingIn = this.filterMovingInTenancies(allReceivedTenancies) as ObjecttenancyTenancy[]
            this.sameTenanciesMovingOut = this.filterMovingOutTenancies(allReceivedTenancies) as ObjecttenancyTenancy[]

            const objectIds = [...new Set(allReceivedTenancies.map(x => x.objectId))]
            if (objectIds.length > 0) {
              const rspObjectsByIds = await ObjectApi.listObjectsByIds(objectIds)
              const allReceivedObjects = rspObjectsByIds.objects
              for (let index = 0; index < allReceivedObjects.length; index++) {
                if (this.objectsWithSameTenants.map(x => x.id).includes(allReceivedObjects[index].id)) {
                  continue
                }
                this.objectsWithSameTenants.push(allReceivedObjects[index])
                this.availableObjects.push(allReceivedObjects[index])
                const listObjectId = [allReceivedObjects[index].id]
                const rspTenancies2 = await ObjectApi.getTenancies(listObjectId)
                if (rspTenancies2.tenancies.length > 0) {
                  this.allTenancies = this.allTenancies.concat(rspTenancies2.tenancies)
                  const otherTenancies = rspTenancies2.tenancies as ObjecttenancyTenancy[]

                  const additionalMovingIn = this.filterMovingInTenancies(otherTenancies) as ObjecttenancyTenancy[]
                  const additionalMovingOut = this.filterMovingOutTenancies(otherTenancies) as ObjecttenancyTenancy[]

                  this.sameTenanciesMovingIn = this.sameTenanciesMovingIn.concat(additionalMovingIn)
                  this.sameTenanciesMovingOut = this.sameTenanciesMovingOut.concat(additionalMovingOut)

                  const listContactIds = otherTenancies.map(x => x.tenant.contactIds).flat().filter((v, i, a) => a.findIndex(t => (t === v)) === i)
                  const contactIds = [...new Set(listContactIds)]
                  if (contactIds.length > 0) {
                    const rspContacts2 = await ContactApi.contactResolve(contactIds)
                    const list = [...this.detailsOfTenants, ...rspContacts2.persons, ...rspContacts2.companies]
                    //   check list and remove duplicates
                    this.detailsOfTenants = list.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
                  }
                }
              }
            }

            if (this.handoverId) {
              // Pre-select tenancies for all objects in the handover
              this.handoverObject.movingInTenancyReferences.forEach(ref => {
                const tenancy =
                  this.sameTenanciesMovingIn.find(x => x.id === ref.tenancyId && x.objectId === ref.objectId) ||
                  this.allTenancies.find(x => x.id === ref.tenancyId && x.objectId === ref.objectId)
                if (tenancy) {
                  this.selectedTenancyMovingIn.push({
                    tenancy: JSON.parse(JSON.stringify(tenancy)),
                    objectId: ref.objectId
                  })
                }
              })

              this.handoverObject.movingOutTenancyReferences.forEach(ref => {
                const tenancy = this.sameTenanciesMovingOut.find(x => x.id === ref.tenancyId && x.objectId === ref.objectId)
                if (tenancy) {
                  this.selectedTenancyMovingOut.push({
                    tenancy: JSON.parse(JSON.stringify(tenancy)),
                    objectId: ref.objectId
                  })
                }
              })

              this.preselectedContactPersons = this.handoverObject.participants
            }

            this.loadDevices(this.preselectedObjectIds)
            this.loading = false
          }
        }
        this.loading = false
      } catch (error) {
        this.loading = false
        console.log(error)
        Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
      } finally {
        this.loadRooms()
        this.loading = false
        this.$nextTick(() => {
          this.dataLoaded = true
        })
      }
    },
    addInitKey() {
      this.initKey = this.initKey + 1
    },
    moveInTenancySelected(tenancy: ObjecttenancyTenancy | null, objectId: string) {
      this.isFormDirty = true
      if (this.selectedTenancyMovingIn?.find(x => x.objectId === objectId)) {
        // update tenancy of selectedTenancyMovingIn by object id
        if (tenancy) {
          this.$set(this.selectedTenancyMovingIn, this.selectedTenancyMovingIn.findIndex((tenancy) => tenancy.objectId === objectId), {
            tenancy: JSON.parse(JSON.stringify(tenancy)),
            objectId: objectId
          })
        } else {
          this.selectedTenancyMovingIn = this.selectedTenancyMovingIn.filter(x => x.objectId !== objectId)
        }
      } else {
        this.selectedTenancyMovingIn.push({ tenancy: JSON.parse(JSON.stringify(tenancy)), objectId: objectId })
      }
    },
    moveInContactPersonsSelected(list: ObjectobjectObject[]) {
      this.listMovingInContactPersons = list
    },
    moveOutTenancySelected(tenancy: ObjecttenancyTenancy | null, objectId: string) {
      this.isFormDirty = true
      if (this.selectedTenancyMovingOut?.find(x => x.objectId === objectId)) {
        // update tenancy of selectedTenancyMovingOut by object id
        if (tenancy) {
          this.$set(this.selectedTenancyMovingOut, this.selectedTenancyMovingOut.findIndex((tenancy) => tenancy.objectId === objectId), {
            tenancy: JSON.parse(JSON.stringify(tenancy)),
            objectId: objectId
          })
        } else {
          this.selectedTenancyMovingOut = this.selectedTenancyMovingOut.filter(x => x.objectId !== objectId)
        }
      } else {
        this.selectedTenancyMovingOut.push({ tenancy: JSON.parse(JSON.stringify(tenancy)), objectId: objectId })
      }
    },
    moveOutContactPersonsSelected(list: ObjectobjectObject[]) {
      this.listMovingOutContactPersons = list
    },
    contactUpdated(contact: ObjectobjectObject) {
      // change contact on detailsOfTenants by id
      const index = this.detailsOfTenants.findIndex(x => x.id === contact.id)
      if (index !== -1) {
        this.$set(this.detailsOfTenants, index, contact)
      } else {
        this.detailsOfTenants.push(contact)
      }
    },
    getListTimes() {
      let hours, minutes
      const list = []
      for (let i = 360; i <= 1425; i += 15) {
        hours = Math.floor(i / 60)
        minutes = i % 60
        if (minutes < 10) {
          minutes = '0' + minutes // adding leading zero
        }
        if (hours < 10) {
          hours = '0' + hours // adding leading zero
        }
        if (hours === 24) {
          hours = '00'
        }
        list.push(hours + ':' + minutes)
      }
      this.timeList = list
    },
    loadDevices(objectIds?: string[]): Promise<void> {
      const requestedIds = [...new Set(objectIds || this.preselectedObjectIds)].filter(id => !!id)
      const idsToFetch = requestedIds.filter(id => !this.loadedDeviceReferenceIds.includes(id))
      if (idsToFetch.length === 0) {
        return this.devicesLoadPromise || Promise.resolve()
      }

      this.loadedDeviceReferenceIds = this.loadedDeviceReferenceIds.concat(idsToFetch)

      const request = ObjectApi.getDevicesByReferenceIds(idsToFetch)
        .then(response => {
          const incoming = response.devices || []
          const savedUnassignedOrder = new Map(
            (this.handoverObject.devicesWithoutRoom || []).map((device, index) => [
              device.id,
              this.effectiveDeviceOrder(device.order ?? null) ?? index + 1
            ])
          )
          incoming.forEach(device => {
            const savedOrder = savedUnassignedOrder.get(device.id)
            if (savedOrder && !device.roomId) {
              device.order = savedOrder
            }
            const index = this.listDevices.findIndex(d => d.id && d.id === device.id)
            if (index !== -1) {
              this.$set(this.listDevices, index, device)
            } else {
              this.listDevices.push(device)
            }
          })

          if (!this.handoverId && idsToFetch.includes(this.objectId)) {
            this.devicesWithoutRooms = this.listDevices.filter(device =>
              !device.roomId &&
              device.referenceId === this.objectId
            )
          } else if (this.handoverId && this.devicesWithoutRooms.length > 0) {
            this.devicesWithoutRooms = this.devicesWithoutRooms.filter(d => {
              if (!d.id?.startsWith('device_')) {
                return true
              }
              const fresh = incoming.find(ld => ld.id === d.id)
              if (!fresh) {
                return true
              }
              return !fresh.roomId
            })
          }
        })
        .catch(e => {
          this.loadedDeviceReferenceIds = this.loadedDeviceReferenceIds.filter(id => !idsToFetch.includes(id))
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.device'), { type: 'error' })
        })

      this.devicesLoadPromise = Promise.all([this.devicesLoadPromise || Promise.resolve(), request]).then(() => undefined)
      return this.devicesLoadPromise
    },
    async awaitDevicesLoaded() {
      if (this.devicesLoadPromise) {
        await this.devicesLoadPromise
      }
    },
    loadRooms() {
      const ids = this.objectsWithSameTenants.map(x => x.id)
      if (ids.length === 0) {
        return
      }
      RoomApi.getRoomsByReferenceIds(ids)
        .then(response => {
          this.rooms = this.rooms.concat(response.rooms)
          if (this.selectedObjectToProcess.length > 0) {
            this.updateSelectedRooms()
          }
          this.loading = false
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadRoomByIds(rooms: string[], templateId: string) {
      if (rooms.length === 0) {
        return Promise.resolve()
      }
      return RoomApi.listById(rooms)
        .then(response => {
          if (templateId && this.loadedTemplatesData[templateId]) {
            // Store template rooms data
            const cachedRooms = this.loadedTemplatesData[templateId].rooms
            const newRooms = response.rooms.filter(newRoom =>
              !cachedRooms.some(existingRoom => existingRoom.id === newRoom.id)
            )
            this.loadedTemplatesData[templateId].rooms = cachedRooms.concat(newRooms)
          }

          const newRooms = response.rooms.filter(newRoom =>
            !this.templateRooms.some(existingRoom => existingRoom.id === newRoom.id)
          )
          this.templateRooms = this.templateRooms.concat(newRooms)
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
          throw e
        })
    },
    loadDevicesByIds(devices: string[], templateId: string) {
      if (devices.length === 0) {
        this.devicesLoadFailed = false
        return Promise.resolve()
      }
      return ObjectApi.listDevicesByIds(devices)
        .then(response => {
          if (templateId && this.loadedTemplatesData[templateId]) {
            // Store template devices data
            const cachedDevices = this.loadedTemplatesData[templateId].devices
            const newDevices = response.devices.filter(newDevice =>
              !cachedDevices.some(existingDevice => existingDevice.id === newDevice.id)
            )
            this.loadedTemplatesData[templateId].devices = cachedDevices.concat(newDevices)
          }

          const newDevices = response.devices.filter(newDevice =>
            !this.templateDevices.some(existingDevice => existingDevice.id === newDevice.id)
          )
          this.templateDevices = this.templateDevices.concat(newDevices)
          this.devicesLoadFailed = false
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.deviceRefresh'), { type: 'error', duration: 5000 })
          this.devicesLoadFailed = true
        })
    },
    storeSelectedRooms(rooms: ProcessHandoverRoom[], object: ObjectobjectObject, from: string) {
      if (rooms.length > 0 && (this.activeTab === 0 || (this.activeTab % 3 === 2 && from === 'device') || (this.activeTab % 3 === 1 && from === 'rooms'))) {
        if (this.selectedRooms.find((room) => room.object === object.id)) {
          // update room of selected room by object id
          this.$set(this.selectedRooms, this.selectedRooms.findIndex((room) => room.object === object.id), {
            rooms: rooms,
            object: object.id
          })
        } else {
          this.selectedRooms.push({ rooms: rooms, object: object.id })
        }
      }
    },
    storeDeletedRooms(rooms: string[], object: ObjectobjectObject) {
      const item = this.deletedRooms.find((room) => room.object === object.id)
      if (item) {
        this.deletedRooms.find((room) => room.object === object.id).rooms.concat(rooms)
      } else {
        this.deletedRooms.push({ rooms: rooms, object: object.id })
      }
      // list of devices that are have roomId = one of the rooms deleted
      const devices = this.listDevices.filter(x => rooms.map(r => r).includes(x.roomId))
      this.storeDeletedDevices(devices.map(d => d.id), object)
    },
    storeDeletedDevices(deletedIds: string[], object: ObjectobjectObject) {
      const item = this.deletedDevices.find((device) => device.object === object.id)
      if (item) {
        for (const id of deletedIds) {
          if (!item.devices.includes(id)) {
            item.devices.push(id)
          }
        }
      } else {
        this.deletedDevices.push({ devices: deletedIds, object: object.id })
      }
    },
    loadBuilding(buildingId: string) {
      ObjectApi.getBuilding(buildingId)
        .then(response => {
          this.building = response.building
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        })
    },
    confirmCancellation() {
      this.confirmCancellationProcess = true
      if (this.nextToPath !== null) {
        this.$router.push({
          name: this.nextToPath.name,
          params: this.nextToPath.params,
          query: this.nextToPath.query
        }, () => {
        })
      } else {
        this.$router.push({ path: this.prevRoutePath, query: this.prevRouteQuery }, () => {
        })
      }
    },
    async handleTabChange(prevIndex: number, nextIndex: number) {
      this.activeTab = nextIndex
      this.preventChangesInSelectedObjectToProcess = nextIndex !== 0
      if (this.disabledSave || this.confirmCancellationProcess) {
        return
      }
      if (this.devicesLoadFailed && nextIndex > 0) {
        this.$nextTick(() => {
          this.activeTab = prevIndex
          this.preventChangesInSelectedObjectToProcess = prevIndex !== 0
          this.$refs['wizard-form'].initializeTabs()
        })
        return
      }
      if (this.navigateVieButtons) {
        this.navigateVieButtons = false
        return
      }
      this.$v.$touch()
      if (this.invalidMovingTenancies) {
        this.$nextTick(() => {
          this.activeTab = prevIndex
          this.preventChangesInSelectedObjectToProcess = prevIndex !== 0
          this.$refs['wizard-form']?.initializeTabs()
        })
        Vue.toasted.show(this.$t('message.savingErrors.handoverProtocolMovingTenancy'), { type: 'error' })
        return
      }
      if (this.draftStatus) {
        this.draftStatus = false
        return
      }
      if (prevIndex === 0) {
        await this.awaitDevicesLoaded()
      }
      if (prevIndex !== 0) {
        this.disabledSave = true
      }
      await this.saveStep({ activeTabIndex: prevIndex, isLastStep: false })
      this.disabledSave = false
    },
    updateSelectedRooms() {
      this.selectedRooms = []
      this.selectedObjectToProcess.forEach(element => {
        this.selectedRooms.push({
          rooms: this.rooms.filter(r => r.referenceId === element.id),
          object: element.id
        })
      })
    },
    selectedObject(object: ObjectobjectObject[]) {
      this.isFormDirty = true
      this.selectedObjectToProcess = object
      this.updateSelectedRooms()
      this.loadDevices(object.map(o => o.id))
    },
    storeDevicesWithoutRooms(devices: ProcessHandoverDevice[], object: ObjectobjectObject | null, from: string) {
      this.isFormDirty = true
      if ((this.activeTab === 3 && from === 'device') || (this.activeTab === 2 && from === 'keys')) {
        return
      }

      const compareIds = (d1: ProcessHandoverDevice, d2: ProcessHandoverDevice) => (d1.id && d2.id && d1.id === d2.id) || (d1.internalId && d2.internalId && d1.internalId === d2.internalId)

      const currentObjectDevices = [...devices].filter(device =>
        !this.deletedDevices.some(deleted => deleted.devices.some(dId => [device.id, device.internalId].includes(dId))) &&
        !this.globalDeletedDevicesIds.includes(device.id) &&
        !device.roomId
      ).reduce((unique, device) => {
        return unique.some(d => compareIds(d, device)) ? unique : [...unique, device]
      }, [])

      if (from === 'keys') {
        // When updating from keys step, only replace keys - preserve ALL non-key devices
        const existingNonKeyDevices = this.devicesWithoutRooms.filter(x => !this.isKeyType(x.type))
        const otherObjectKeys = this.devicesWithoutRooms.filter(x => this.isKeyType(x.type) && x.referenceId !== object?.id)
        const newKeys = currentObjectDevices.filter(d => this.isKeyType(d.type))
        this.devicesWithoutRooms = [...existingNonKeyDevices, ...otherObjectKeys, ...newKeys]
      } else {
        const otherObjectsDevices = this.devicesWithoutRooms.filter(x => x.referenceId !== object?.id)
        const existingCurrentObjectDevices = this.devicesWithoutRooms.filter(x => x.referenceId === object?.id)

        if (currentObjectDevices.length === 0 && existingCurrentObjectDevices.some(d => d.id?.startsWith('device_'))) {
          return
        }

        const existingKeys = existingCurrentObjectDevices.filter(d => this.isKeyType(d.type))
        const currentDevices = currentObjectDevices.filter(d => !this.isKeyType(d.type))
        this.devicesWithoutRooms = [...otherObjectsDevices, ...currentDevices, ...existingKeys]
      }
    },

    async nextStep(props) {
      if (this.devicesLoadFailed && props.activeTabIndex === 0) {
        return
      }
      // Block if already processing (prevent multiple clicks)
      if (this.disabledSave) {
        return
      }
      // Set disabled to prevent multiple clicks during next step, including the
      this.disabledSave = true
      this.navigateVieButtons = true
      this.activeTabIndex = props.activeTabIndex + 1
      if (props.activeTabIndex === 0) {
        this.$v.$touch()
        if (this.$v.$invalid && !this.$v.scheduledTime.isNotDuplicated) {
          this.disabledSave = false
          Vue.toasted.show(this.$t('message.savingErrors.secondHandoverSameDateTime'), { type: 'error' })
          return
        }
        if (this.scheduledTime === '' || this.scheduledDate === '') {
          this.disabledSave = false
          return
        }
        if (this.invalidMovingTenancies) {
          this.disabledSave = false
          Vue.toasted.show(this.$t('message.savingErrors.handoverProtocolMovingTenancy'), { type: 'error' })
          return
        }
        this.handoverObject.scheduledTime = {
          hours: parseInt(this.scheduledTime.substr(0, 2)),
          minutes: parseInt(this.scheduledTime.substr(3, 2))
        }
        this.handoverObject.scheduledDate = {
          year: parseInt(this.scheduledDate.substr(0, 4)),
          month: parseInt(this.scheduledDate.substr(5, 2)),
          day: parseInt(this.scheduledDate.substr(8, 2))
        }
        await this.awaitDevicesLoaded()
        this.disabledSave = false
        props.nextTab()
        this.scrollToTop()
      } else {
        await this.saveStep(props)
        this.$nextTick(() => {
          if (props.isLastStep) {
            this.confirmCancellationProcess = true
          } else {
            this.disabledSave = false
          }
          props.nextTab()
          this.scrollToTop()
        })
      }
    },
    async saveStep(props, draft = false, back = false) {
      // Block if already processing (prevent multiple clicks)
      if (draft && this.disabledSave) {
        return
      }
      // Set disabled to prevent multiple clicks during save draft
      if (draft) {
        this.disabledSave = true
      }
      this.draftStatus = draft
      if (props.activeTabIndex === 0 && draft) {
        this.$v.$touch()
        if (this.$v.$invalid && !this.$v.scheduledTime.isNotDuplicated) {
          this.disabledSave = false
          this.draftStatus = false
          Vue.toasted.show(this.$t('message.savingErrors.secondHandoverSameDateTime'), { type: 'error' })
          return
        }
        if (this.scheduledTime === '' || this.scheduledDate === '') {
          this.disabledSave = false
          this.draftStatus = false
          return
        }
        if (this.invalidMovingTenancies) {
          this.disabledSave = false
          this.draftStatus = false
          Vue.toasted.show(this.$t('message.savingErrors.handoverProtocolMovingTenancy'), { type: 'error' })
          return
        }
        this.handoverObject.scheduledTime = {
          hours: parseInt(this.scheduledTime.substr(0, 2)),
          minutes: parseInt(this.scheduledTime.substr(3, 2))
        }
        this.handoverObject.scheduledDate = {
          year: parseInt(this.scheduledDate.substr(0, 4)),
          month: parseInt(this.scheduledDate.substr(5, 2)),
          day: parseInt(this.scheduledDate.substr(8, 2))
        }
      } else if (props.activeTabIndex % 3 === 1) {
        if (this.selectedRooms.length > 0) {
          const indexOfSteps = Math.floor((props.activeTabIndex - 1) / 3)
          const roomToUpdate = this.selectedRooms[indexOfSteps].rooms.filter(room => room.edited)
          let roomToDelete = this.deletedRooms.find(r => r.object === this.selectedRooms[indexOfSteps].object) || []
          if (roomToDelete && roomToDelete.rooms?.length > 0) {
            roomToDelete = roomToDelete.rooms.map(r => r)
            // remove the rooms that are not started with room_
            roomToDelete = roomToDelete.filter(r => typeof r === 'string' && r.startsWith('room_'))
          }
          let deviceToDelete = this.deletedDevices.find(r => r.object === this.selectedRooms[indexOfSteps].object) || []
          if (deviceToDelete && deviceToDelete.devices?.length > 0) {
            deviceToDelete = deviceToDelete.devices.map(r => r)
            deviceToDelete = deviceToDelete.filter(r => typeof r === 'string' && r.startsWith('device_'))
          }
          const roomToAdd = this.selectedRooms[indexOfSteps].rooms.filter(room => typeof room.id !== 'string' || !room?.id?.startsWith('room_'))
          const promises = []
          if (roomToUpdate.length > 0) {
            promises.push(RoomApi.updateRooms(roomToUpdate))
          }
          if (roomToAdd.length > 0) {
            promises.push(RoomApi.createRooms(roomToAdd))
          }
          if (roomToDelete.length > 0) {
            promises.push(RoomApi.deleteRooms(roomToDelete))
          }
          if (deviceToDelete.length > 0) {
            deviceToDelete.forEach((device) => {
              promises.push(ObjectApi.deleteDevice(device))
            })
          }
          await Promise.all(promises)
            .then(responses => {
              if (deviceToDelete.length > 0) {
                Vue.toasted.show(this.$t('message.successMessages.deviceDeleted'), { type: 'success' })
                this.globalDeletedDevicesIds = this.globalDeletedDevicesIds.concat(deviceToDelete)
              }
              const currentObject = this.selectedRooms[indexOfSteps].object
              const deletedRoomsIds = this.deletedRooms.find(r => r.object === currentObject)?.rooms?.map(r => r) || []
              this.rooms = this.rooms.filter(d => !d.internalId && !deletedRoomsIds.some(r => r === d.id))
              if (responses.length > 0) {
                // update every room from selectedRoom with the new from response
                this.selectedRooms[indexOfSteps].rooms.forEach((room, indexRoom) => {
                  if (room.edited) {
                    this.selectedRooms[indexOfSteps].rooms[indexRoom] = responses[0].rooms?.find(r => r.id === room.id) ? responses[0].rooms?.find(r => r.id === room.id) : responses[1].rooms?.find(r => r.id === room.id)
                  }
                })
                this.selectedRooms[indexOfSteps].rooms = this.selectedRooms[indexOfSteps].rooms.filter(r => typeof r.id === 'string' && r.id.startsWith('room_'))
                responses.forEach((response) => {
                  if (response?.rooms) {
                    this.selectedRooms[indexOfSteps].rooms = this.selectedRooms[indexOfSteps].rooms.concat(response.rooms)
                    this.rooms = this.rooms.concat(response.rooms)
                  }
                })
                // remove delete rooms from selectedrooms
                this.selectedRooms[indexOfSteps].rooms = this.selectedRooms[indexOfSteps].rooms.filter(r => !roomToDelete.includes(r.id))
                // remove duplicates from selectedRooms
                this.selectedRooms[indexOfSteps].rooms = this.selectedRooms[indexOfSteps].rooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
                this.$nextTick(() => {
                  this.deletedRooms = []
                })
              }
            }).catch(e => {
              console.log(e)
              Vue.toasted.show(this.$t('message.savingErrors.onboarding'), { type: 'error' })
            })
        }
      } else if (props && props.activeTabIndex !== undefined && props.activeTabIndex % 3 === 2) {
        const indexOfStep = Math.floor((props.activeTabIndex - 2) / 3)
        const currentObject = this.selectedRooms[indexOfStep].object
        // create the new devices and update the edited devices
        const devicesToAdd = []
        const devicesToUpdate = []
        let deviceToDelete = this.deletedDevices.find(r => r.object === currentObject) || []
        if (deviceToDelete && deviceToDelete.devices?.length > 0) {
          deviceToDelete = deviceToDelete.devices.map(r => r)
          deviceToDelete = deviceToDelete.filter(r => typeof r === 'string' && r.startsWith('device_'))
          // remove the device from the list if it exists on globalDeletedDevicesIds
          deviceToDelete = deviceToDelete.filter(d => !this.globalDeletedDevicesIds.some(r => r === d))
        }
        for (let index = 0; index < this.selectedRooms[indexOfStep].rooms.length; index++) {
          if (this.selectedRooms[indexOfStep].rooms[index].devices) {
            this.selectedRooms[indexOfStep].rooms[index].devices.forEach((device) => {
              device.roomId = this.selectedRooms[indexOfStep].rooms[index].id
              device.referenceId = this.selectedRooms[indexOfStep].rooms[index].referenceId
              if (device.internalId || !device.id.startsWith('device_')) {
                devicesToAdd.push(JSON.parse(JSON.stringify(device)))
              } else if (device.edited && device.id) {
                devicesToUpdate.push(JSON.parse(JSON.stringify(device)))
              }
            })
          }
        }
        for (let index = 0; index < this.devicesWithoutRooms.filter(x => x.referenceId === currentObject).length; index++) {
          if (this.devicesWithoutRooms.filter(x => x.referenceId === currentObject)[index].internalId || !this.devicesWithoutRooms.filter(x => x.referenceId === currentObject)[index].id.startsWith('device_')) {
            devicesToAdd.push(this.devicesWithoutRooms.filter(x => x.referenceId === currentObject)[index])
          } else if (this.devicesWithoutRooms.filter(x => x.referenceId === currentObject)[index].edited) {
            devicesToUpdate.push(this.devicesWithoutRooms.filter(x => x.referenceId === currentObject)[index])
          }
        }
        const promises = []
        if (this.isSaving) {
          return
        }
        if (devicesToAdd.length > 0) {
          devicesToAdd.forEach((device) => {
            promises.push(ObjectApi.createDevice(device))
          })
        }
        if (devicesToUpdate.length > 0) {
          devicesToUpdate.forEach((device) => {
            promises.push(ObjectApi.updateDevice(device))
          })
        }
        if (deviceToDelete.length > 0) {
          deviceToDelete.forEach((device) => {
            promises.push(ObjectApi.deleteDevice(device))
          })
        }
        await Promise.all(promises)
          .then(responses => {
            if (deviceToDelete.length > 0) {
              Vue.toasted.show(this.$t('message.successMessages.deviceDeleted'), { type: 'success' })
            }
            const deletedDevicesIds = this.deletedDevices.find(r => r.object === currentObject)?.devices?.map(r => r) || []
            this.globalDeletedDevicesIds = this.globalDeletedDevicesIds.concat(deviceToDelete)
            this.listDevices = this.listDevices.filter(d => !d.internalId && !deletedDevicesIds.some(r => r === d.id))
            if (responses.length > 0) {
              const unassignedDevices = []
              responses.map(item => item.device).forEach((device) => {
                if (device) {
                  this.listDevices = this.listDevices.filter(d => d.id !== device.id)
                  this.listDevices.push(device)
                  if (device?.roomId === '') {
                    unassignedDevices.push(device)
                  }
                }
              })
              if (unassignedDevices.length > 0) {
                this.devicesWithoutRooms = JSON.parse(JSON.stringify(this.devicesWithoutRooms.filter(x => !x.internalId)))
                unassignedDevices.forEach((device) => {
                  if (!this.devicesWithoutRooms.find(x => x.id === device.id)) {
                    this.devicesWithoutRooms.push(device)
                  } else {
                    const index = this.devicesWithoutRooms.findIndex(x => x.id === device.id)
                    if (index !== -1) {
                      this.$set(this.devicesWithoutRooms, index, device)
                    }
                  }
                })
                this.resetDevicesWithoutRooms = true
              }
              for (let index = 0; index < this.selectedRooms[indexOfStep].rooms.length; index++) {
                if (this.selectedRooms[indexOfStep].rooms[index].devices) {
                  // remove the device with internalId
                  this.selectedRooms[indexOfStep].rooms[index].devices = this.selectedRooms[indexOfStep].rooms[index].devices.filter(d => !d.internalId)
                  // add the new devices for the room by roomId
                  responses.map(item => item.device).forEach((device) => {
                    if (device?.roomId === this.selectedRooms[indexOfStep].rooms[index].id) {
                      device.value = device.type === '' || device.type === null || device.type === 'DEVICE_TYPE_UNDEFINED' ? '-' : this.$t('message.onBoarding.devices.typeValues.' + device.type)
                      const deviceIndex = this.selectedRooms[indexOfStep].rooms[index].devices.findIndex(d => d.id === device.id)
                      if (deviceIndex !== -1) {
                        this.$set(this.selectedRooms[indexOfStep].rooms[index].devices, deviceIndex, device)
                      } else {
                        this.selectedRooms[indexOfStep].rooms[index].devices.push(device)
                      }
                    }
                  })
                  // this.selectedRooms[(props.activeTabIndex / 2) - 1].rooms[index].devices = this.selectedRooms[(props.activeTabIndex / 2) - 1].rooms[index].devices.concat(responses.map(item => item.device).filter(r => r.roomId === this.selectedRooms[(props.activeTabIndex / 2) - 1].rooms[index].id))
                }
              }
              this.$nextTick(() => {
                this.deletedDevices = []
              })
            } else {
              this.$nextTick(() => {
                this.deletedDevices = []
              })
            }
          }).catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.onboarding'), { type: 'error' })
          })
          .finally(() => {
            // set the newest value of selectedRooms
            this.selectedRooms = JSON.parse(JSON.stringify(this.selectedRooms))
          })
      } else if (props.activeTabIndex % 3 === 0 && props.activeTabIndex !== 0) {
        // step 4
        const indexOfStep = Math.floor((props.activeTabIndex - 3) / 3)
        const currentObject = this.selectedRooms[indexOfStep].object
        // create the new devices and update the edited devices
        const devicesToAdd = []
        const devicesToUpdate = []
        let deviceToDelete = this.deletedDevices.find(r => r.object === currentObject) || []
        if (deviceToDelete && deviceToDelete.devices?.length > 0) {
          deviceToDelete = deviceToDelete.devices.map(r => r)
          deviceToDelete = deviceToDelete.filter(r => typeof r === 'string' && r.startsWith('device_'))
          // remove the device from the list if it exists on globalDeletedDevicesIds
          deviceToDelete = deviceToDelete.filter(d => !this.globalDeletedDevicesIds.some(r => r === d))
        }
        for (let index = 0; index < this.devicesWithoutRooms.filter(x => x.referenceId === currentObject).length; index++) {
          const device = this.devicesWithoutRooms.filter(x => x.referenceId === currentObject)[index]
          if (device) {
            if (!device.id?.startsWith('device_')) {
              devicesToAdd.push(device)
            } else if (device.edited) {
              devicesToUpdate.push(device)
            }
          }
        }
        const promises = []
        if (devicesToAdd.length > 0) {
          devicesToAdd.forEach((device) => {
            promises.push(ObjectApi.createDevice(device))
          })
        }
        if (devicesToUpdate.length > 0) {
          devicesToUpdate.forEach((device) => {
            promises.push(ObjectApi.updateDevice(device))
          })
        }
        if (deviceToDelete.length > 0) {
          deviceToDelete.forEach((device) => {
            promises.push(ObjectApi.deleteDevice(device))
          })
        }
        await Promise.all(promises)
          .then(responses => {
            if (responses.length > 0) {
              if (deviceToDelete.length > 0) {
                Vue.toasted.show(this.$t('message.successMessages.deviceDeleted'), { type: 'success' })
              }
              const deletedDevicesIds = this.deletedDevices.find(r => r.object === currentObject)?.devices?.map(r => r) || []
              this.globalDeletedDevicesIds = this.globalDeletedDevicesIds.concat(deviceToDelete)
              this.listDevices = this.listDevices.filter(d => !d.internalId && !deletedDevicesIds.some(r => r === d.id))
              if (responses.length > 0) {
                const unassignedDevices = []
                responses.map(item => item.device).forEach((device) => {
                  if (device) {
                    this.listDevices = this.listDevices.filter(d => d.id !== device.id)
                    this.listDevices.push(device)
                    if (!device.roomId) {
                      unassignedDevices.push(device)
                    }
                  }
                })
                if (unassignedDevices.length > 0) {
                  this.devicesWithoutRooms = JSON.parse(JSON.stringify(this.devicesWithoutRooms.filter(x => !x.internalId)))
                  unassignedDevices.forEach((device) => {
                    if (!this.devicesWithoutRooms.find(x => x.id === device.id)) {
                      this.devicesWithoutRooms.push(device)
                    } else {
                      const index = this.devicesWithoutRooms.findIndex(x => x.id === device.id)
                      if (index !== -1) {
                        this.$set(this.devicesWithoutRooms, index, device)
                      }
                    }
                  })
                }
              }
              this.$nextTick(() => {
                const deletedDeviceIdSet = new Set(this.deletedDevices.find(r => r.object === currentObject)?.devices || [])
                this.devicesWithoutRooms = JSON.parse(JSON.stringify(
                  this.devicesWithoutRooms.filter(x => x.id?.startsWith('device_') && !deletedDeviceIdSet.has(x.id))
                ))
                this.deletedDevices = []
                if (props.isLastStep) {
                  this.confirmCancellationProcess = true
                  if (!draft && !back) {
                    this.saveHandoverProcess(draft)
                  }
                }
              })
            } else {
              this.$nextTick(() => {
                this.deletedDevices = []
                if (props.isLastStep) {
                  this.confirmCancellationProcess = true
                  if (!draft && !back) {
                    this.saveHandoverProcess(draft)
                  }
                }
              })
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.onboarding'), { type: 'error' })
          })
      }
      if (draft) {
        this.$nextTick(() => {
          this.saveHandoverProcess(draft)
        })
      }
    },
    async backClicked(props) {
      // Block if already processing (prevent multiple clicks)
      if (this.disabledSave) {
        return
      }
      this.disabledSave = true
      this.navigateVieButtons = true
      await this.saveStep(props, false, true)
      this.$nextTick(() => {
        props.prevTab()
        this.scrollToTop()
        this.disabledSave = false
      })
    },
    scrollToTop() {
      this.$nextTick(() => {
        const domRect = this.$el.querySelector('#wizard')
        if (!domRect) {
          return
        }
        domRect.scrollIntoView(true)
      })
    },
    effectiveDeviceOrder(...orders: Array<number | null>): number | null {
      return orders.find(value => value !== null && value >= 1) ?? null
    },
    mapDeviceConditionToHandoverCondition(deviceCondition: string): string {
      switch (deviceCondition) {
        case 'DEVICE_CONDITION_UNDEFINED':
        case 'DEVICE_CONDITION_REPAIR_NEEDED':
        case 'DEVICE_CONDITION_REPLACEMENT_NEEDED':
          return 'CONDITION_UNDEFINED'
        case 'DEVICE_CONDITION_PERFECT':
          return 'CONDITION_PERFECT'
        case 'DEVICE_CONDITION_NORMAL_WEAR':
          return 'CONDITION_NORMAL_WEAR'
        case 'DEVICE_CONDITION_DEFICIENCY':
          return 'CONDITION_DEFICIENCY'
        default:
          return 'CONDITION_UNDEFINED'
      }
    },
    isHandoverDeviceDeleted(deviceId: string, objectId: string): boolean {
      const deletedIds = this.deletedDevices.find(entry => entry.object === objectId)?.devices || []
      return deletedIds.includes(deviceId) || this.globalDeletedDevicesIds.includes(deviceId)
    },
    getRoomDevicesForSave(room: ProcessHandoverRoom, objectId: string): ProcessHandoverDevice[] {
      const fromRoomState = (room.devices || []).filter(device => !device.uninstallation)
      const fromListDevices = this.listDevices.filter(device =>
        device.roomId === room.id &&
        device.referenceId === objectId &&
        device.id?.startsWith('device_') &&
        !device.uninstallation &&
        !this.isKeyType(device.type) &&
        !this.isHandoverDeviceDeleted(device.id, objectId)
      )

      const mergedDevices = [...fromRoomState]
      fromListDevices.forEach(device => {
        if (!mergedDevices.some(d => d.id === device.id || (d.internalId && d.internalId === device.internalId))) {
          mergedDevices.push(device)
        }
      })
      return mergedDevices
    },
    findAssigneeContactIdByUserEmail(): string | null {
      const userEmail = this.userEmail?.toLowerCase()
      if (!userEmail) {
        return null
      }
      return this.availableEmployeeContacts.find((e: V2Person) => e.email?.toLowerCase() === userEmail)?.id || null
    },
    createParticipant(contactId: string, type: string) {
      // Check if this participant already exists in the current handover (for updates)
      const existingParticipant = this.handoverId && this.handoverObject.participants
        ? this.handoverObject.participants.find(p => p.contactId === contactId && p.type === type)
        : null

      if (existingParticipant) {
        return {
          contactId: contactId,
          type: type,
          attending: existingParticipant.attending ?? false,
          signed: existingParticipant.signed ?? false,
          powerOfAttorneyMediaIds: existingParticipant.powerOfAttorneyMediaIds ?? []
        }
      }

      return {
        contactId: contactId,
        type: type,
        attending: false,
        signed: false,
        powerOfAttorneyMediaIds: []
      }
    },
    async saveHandoverProcess(draft = false) {
      if (this.invalidMovingTenancies) {
        this.disabledSave = false
        Vue.toasted.show(this.$t('message.savingErrors.handoverProtocolMovingTenancy'), { type: 'error' })
        return
      }

      this.isSaving = true
      await this.awaitDevicesLoaded()

      // Create / update a single handover item for all selected objects
      const handoverItem = {
        ...emptyHandoverProtocol,
        draft: draft,
        assigneeContactId: this.handoverObject.assigneeContactId,
        note: this.handoverObject.note,
        scheduledDate: this.handoverObject.scheduledDate,
        scheduledTime: this.handoverObject.scheduledTime,
        templateId: this.templateId,
        ownerId: this.lastTenancy.ownerId,
        participants: [],
        movingInTenancyReferences: [],
        movingOutTenancyReferences: [],
        devicesWithoutRoom: [],
        rooms: []
      }

      // Collect data from all selected objects into a the handover
      this.selectedObjectToProcess.forEach((object) => {
        const devices = this.devicesWithoutRooms.length > 0
          ? this.devicesWithoutRooms
            .map((device, index) => {
              const fromList = this.listDevices.find(d => d.id === device.id)
              const order = this.effectiveDeviceOrder(device.order ?? null, fromList?.order ?? null) ?? index + 1
              return fromList ? { ...device, ...fromList, order } : { ...device, order }
            })
            .filter(device =>
              device?.referenceId === object.id &&
              device.id?.startsWith('device_') &&
              !device.uninstallation &&
              !device.roomId
            )
          : this.listDevices.filter(r => r.referenceId === object.id && (r.roomId === null || r.roomId === '') && r.id?.startsWith('device_') && !r.uninstallation && !this.isKeyType(r.type))

        const uniqueDevices = devices.filter((device, index, self) => index === self.findIndex((d) => d.id === device.id))
        uniqueDevices.forEach(device => {
          device.installationDate = device.installation
          device.previousCondition = this.mapDeviceConditionToHandoverCondition(device.condition)
          device.keyData = device.lockingKey
          device.keyData.amount = device.amount
          device.keyData.keyNumber = device.serialNumber
          device.keyData.lockingPlanNumber = device.lockingKey.lockingPlanNumber

          if (Array.isArray(device.images)) {
            device.images.forEach(image => {
              image.attachTo = 'ATTACH_TO_DEVICE_ONLY'
            })
          }
          handoverItem.devicesWithoutRoom.push(device)
        })

        // Collect rooms for this object
        const selectedRoom = this.selectedRooms.find(r => r.object === object.id)
        if (selectedRoom) {
          selectedRoom.rooms.forEach((room) => {
            const mappedDevices = this.getRoomDevicesForSave(room, object.id)
              .map(device => ({
                ...device,
                installationDate: device.installation,
                previousCondition: this.mapDeviceConditionToHandoverCondition(device.condition),
                keyData: {
                  ...device.lockingKey,
                  amount: device.amount,
                  lockingPlanNumber: device.lockingKey.lockingPlanNumber,
                  keyNumber: device.serialNumber
                },
                images: Array.isArray(device.images)
                  ? device.images.map(image => ({ ...image, attachTo: 'ATTACH_TO_DEVICE_ONLY' }))
                  : device.images
              }))

            handoverItem.rooms.push({
              id: room.id,
              devices: mappedDevices,
              name: room.name,
              note: room.note || ''
            })
          })
        }

        // Collect participants from tenancies for this object
        if (this.selectedTenancyMovingOut.length > 0) {
          const item = this.selectedTenancyMovingOut.find(x => x.objectId === object.id)
          if (item) {
            item.tenancy.tenant.contactIds.forEach(contactId => {
              handoverItem.participants.push(this.createParticipant(contactId, 'PARTICIPANT_TYPE_TENANT_MOVE_OUT'))
            })
          }
        }

        if (this.selectedTenancyMovingIn.length > 0) {
          const item = this.selectedTenancyMovingIn.find(x => x.objectId === object.id)
          if (item) {
            item.tenancy.tenant.contactIds.forEach(contactId => {
              handoverItem.participants.push(this.createParticipant(contactId, 'PARTICIPANT_TYPE_TENANT_MOVE_IN'))
            })
          }
        }

        // Collect tenancy references for this object
        const movingOutItem = this.selectedTenancyMovingOut.find(x => x.objectId === object.id)
        const movingInItem = this.selectedTenancyMovingIn.find(x => x.objectId === object.id)

        // Validate: same tenancy cannot be both moving in and moving out
        const isSameTenancy = movingOutItem && movingInItem &&
          movingOutItem.tenancy.id === movingInItem.tenancy.id &&
          movingOutItem.objectId === movingInItem.objectId

        if (movingOutItem && !isSameTenancy) {
          handoverItem.movingOutTenancyReferences.push({
            tenancyId: movingOutItem.tenancy.id,
            objectId: movingOutItem.objectId
          })
        }

        if (movingInItem && !isSameTenancy) {
          handoverItem.movingInTenancyReferences.push({
            tenancyId: movingInItem.tenancy.id,
            objectId: movingInItem.objectId
          })
        }
      })

      // Add contact persons (these are shared across all objects)
      if (this.listMovingInContactPersons) {
        this.listMovingInContactPersons.forEach(contact => {
          handoverItem.participants.push(this.createParticipant(contact.id, 'PARTICIPANT_TYPE_TENANT_MOVE_IN'))
        })
      }
      if (this.listMovingOutContactPersons) {
        this.listMovingOutContactPersons.forEach(contact => {
          handoverItem.participants.push(this.createParticipant(contact.id, 'PARTICIPANT_TYPE_TENANT_MOVE_OUT'))
        })
      }

      // administrator participant
      if (this.handoverObject.assigneeContactId) {
        // remove participants with type PARTICIPANT_TYPE_ADMINISTRATOR
        handoverItem.participants = handoverItem.participants.filter(p => p.type !== 'PARTICIPANT_TYPE_ADMINISTRATOR')
        // add administrator participant
        handoverItem.participants.push(this.createParticipant(this.handoverObject.assigneeContactId, 'PARTICIPANT_TYPE_ADMINISTRATOR'))
      }

      // Remove duplicates from all arrays
      handoverItem.devicesWithoutRoom = handoverItem.devicesWithoutRoom.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
      handoverItem.rooms = handoverItem.rooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
      handoverItem.participants = handoverItem.participants.filter((v, i, a) => a.findIndex(t => (t.contactId === v.contactId && t.type === v.type)) === i)

      // Remove duplicates of moving in/out references
      handoverItem.movingOutTenancyReferences = handoverItem.movingOutTenancyReferences.filter((v, i, a) =>
        a.findIndex(t => t.tenancyId === v.tenancyId && t.objectId === v.objectId) === i
      )
      handoverItem.movingInTenancyReferences = handoverItem.movingInTenancyReferences.filter((v, i, a) =>
        a.findIndex(t => t.tenancyId === v.tenancyId && t.objectId === v.objectId) === i
      )

      // Ensure same tenancy is not in both movingIn and movingOut (remove from movingIn if duplicate)
      const movingOutKeys = new Set(handoverItem.movingOutTenancyReferences.map(r => `${r.tenancyId}-${r.objectId}`))
      handoverItem.movingInTenancyReferences = handoverItem.movingInTenancyReferences.filter(r =>
        !movingOutKeys.has(`${r.tenancyId}-${r.objectId}`)
      )

      // Create or update the handover
      const savePromise = this.handoverId
        ? HandoverProtocolApi.updateHandover(this.handoverId, handoverItem)
        : HandoverProtocolApi.createHandover(handoverItem)

      this.confirmCancellationProcess = true
      savePromise.then(() => {
        this.confirmCancellationProcess = true
        Vue.toasted.show(this.$t('message.successMessages.handoverProtocol'), { type: 'success' })
        if (this.prevRoutePath !== '/' && this.prevRouteQuery) {
          this.$router.push({ path: this.prevRoutePath, query: this.prevRouteQuery }, () => {
          })
        } else {
          this.$router.push({
            name: this.isOwnerModule ? 'OwnerBuildingDetailsView' : this.isAssetModule ? 'AssetBuildingDetailsView' : this.isAccountingModule ? 'AccountingBuildingDetailsView' : 'AdminBuildingDetailsView',
            params: { id: this.building.id },
            query: {
              view: 'objects',
              elementSelected: this.objectId
            }
          })
        }
        this.$nextTick(async () => {
          this.getCountOverdueHandovers(true)
        })
      })
      .catch(e => {
        console.log(e)
        Vue.toasted.show(this.$t('message.savingErrors.onboarding'), { type: 'error' })
      })
      .finally(() => {
        this.disabledSave = false
        this.isSaving = false
      })
    },
    addDocumentToTenancy(value: boolean) {
      this.addDocumentToContact = value
    },
    contractSelectedAction(value: string[], contracts: string[], objectId: string) {
      const item: ContractEntry = { value, contracts, objectId }
      const index = this.selectedEntriesContract.findIndex(c => c.objectId === objectId)
      if (index !== -1) {
        this.selectedEntriesContract[index] = item
      } else {
        this.selectedEntriesContract.push(item)
      }
      this.isAnyContract()
    },
    isAnyContract() {
      this.anyContract = false
      this.selectedEntriesContract.forEach((entry) => {
        if (entry.value.length > 0) {
          this.anyContract = true
        }
      })
    },
    getTabTitle(type: string, item: ObjectobjectObject): string {
      const tabTitle = this.$t(`message.processHandover.${type}.title`)
      if (this.selectedObjectToProcess.length > 1) {
        return `${tabTitle} | ${this.$t('message.onBoarding.buildings.objects.category.' + item.category.toLowerCase())} | ${item.numericId}`
      }
      return tabTitle
    },
    getPreselectedTenancyForObject(objectId: string, direction: string): ObjecttenancyTenancy | null {
      if (direction === 'moveIn') {
        const selected = this.selectedTenancyMovingIn.find(x => x.objectId === objectId)
        return selected ? selected.tenancy : null
      } else if (direction === 'moveOut') {
        const selected = this.selectedTenancyMovingOut.find(x => x.objectId === objectId)
        return selected ? selected.tenancy : null
      }
      return null
    },
    isObjectTenancyInvalid(objectId: string): boolean {
      if (!this.$v.$dirty) {
        return false
      }
      return (
        !this.selectedTenancyMovingIn.some(x => x.objectId === objectId) &&
        !this.selectedTenancyMovingOut.some(x => x.objectId === objectId)
      )
    }
  },
  validations() {
    return {
      scheduledDate: {
        required
      },
      scheduledTime: {
        required,
        isNotDuplicated
      }
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.wizard-nav-disabled) {
  .wizard-nav {
    pointer-events: none;
    .wizard-progress-with-circle {
      opacity: 0.7;
    }
    .wizard-icon-circle:not(.checked) {
      opacity: 0.9;
    }
  }
}
</style>
